import { useState, useEffect, useCallback, useRef } from 'react';
import { validateUrl as validateUrlFromUtils } from '../utils/submissionHelpers';

/**
 * Custom hook for managing submission form state and validation
 * @param {Object} userCurrentContent - Currently active content
 * @param {string} currentContentType - Type of current content
 * @param {Object} userSubmissionStatus - User submission status from Redux
 * @param {Function} addSubmission - Redux action to add submission
 * @param {Function} updateSubmission - Redux action to update submission
 * @returns {Object} Submission state and handlers
 */
export const useSubmissionState = (
  userCurrentContent,
  currentContentType,
  userSubmissionStatus,
  addSubmission,
  updateSubmission
) => {
  // Submission form state
  const [submissionData, setSubmissionData] = useState({
    link: '',
    comment: '',
    achievementImages: []
  });
  const [collaborators, setCollaborators] = useState([]);
  const [urlError, setUrlError] = useState('');
  const [submissionStatusLoading, setSubmissionStatusLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForStatus, setIsWaitingForStatus] = useState(false);

  // Ref to track submission timeout
  const submittingTimeoutRef = useRef(null);

  // URL validation function that maintains original behavior
  const validateUrlLocal = useCallback((url) => {
    const validation = validateUrlFromUtils(url);
    setUrlError(validation.error);
    return validation.isValid;
  }, []);

  // Handle URL input changes with validation
  const handleUrlChange = useCallback((e) => {
    const value = e.target.value;
    setSubmissionData(prev => ({ ...prev, link: value }));

    // Only validate if user has entered something
    if (value.trim() !== '') {
      // Add small delay to avoid validating while user is still typing
      setTimeout(() => validateUrlLocal(value), 500);
    } else {
      setUrlError('');
    }
  }, [validateUrlLocal]);

  // Get current submission status helper
  const getCurrentSubmissionStatus = useCallback(() => {
    if (!userCurrentContent || currentContentType !== 'submissionForm') {
      return null;
    }
    const status = userSubmissionStatus[userCurrentContent._id];
    return status || null;
  }, [userCurrentContent, currentContentType, userSubmissionStatus]);

  // Check if user can progress from current submission
  const canProgressFromSubmission = useCallback(() => {
    if (currentContentType !== 'submissionForm') {
      return true; // Not a submission form, can progress
    }

    const submissionStatus = getCurrentSubmissionStatus();

    // If no submission yet, cannot progress
    if (!submissionStatus || !submissionStatus.hasSubmission) {
      return false;
    }

    // If automatic approval, can progress immediately
    if (submissionStatus.submission.isAutomatic) {
      return true;
    }

    // For manual approval, only allow progression if approved
    return submissionStatus.submission.status === 'approved';
  }, [currentContentType, getCurrentSubmissionStatus]);

  // Handle submission form submission
  // Accept optional contentOverride for backwards navigation resubmission
  const handleSubmitSubmission = useCallback((contentOverride) => {
    const content = contentOverride || userCurrentContent;
    if (!content) {
      return;
    }

    // Check required fields
    if (content.linkRequired && !submissionData.link.trim()) {
      return;
    }

    // Validate URL format if link is provided
    if (submissionData.link && submissionData.link.trim()) {
      const urlValidationResult = validateUrlLocal(submissionData.link);
      if (!urlValidationResult) {
        return;
      }
    }

    if (content.commentRequired && !submissionData.comment.trim()) {
      return;
    }
    if (content.achievementImageRequired && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) {
      return;
    }

    const submissionDataToSend = {
      submissionFormId: content._id,
      link: submissionData.link.trim(),
      comment: submissionData.comment.trim(),
      achievementImages: submissionData.achievementImages || []
    };

    // Include collaborators for new submissions
    if (collaborators.length > 0) {
      submissionDataToSend.collaborators = collaborators.map(u => u._id);
    }

    // Check if this is an edit/resubmission (rejected, more_info_needed, or pending submission exists)
    const submissionStatus = userSubmissionStatus[content._id];
    const isResubmission = submissionStatus?.hasSubmission && ['rejected', 'more_info_needed', 'pending'].includes(submissionStatus.submission.status);

    // Show submitting state immediately (optimistic UI)
    setIsSubmitting(true);
    setIsWaitingForStatus(true);
    setSubmissionStatusLoading(false);

    // Set timeout fallback to prevent UI from getting stuck
    submittingTimeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
      setIsWaitingForStatus(false);
    }, 5000); // 5 seconds timeout

    if (isResubmission) {
      // Update existing rejected submission
      updateSubmission(submissionStatus.submission._id, submissionDataToSend);
    } else {
      // Create new submission
      addSubmission(submissionDataToSend);
    }

    // Clear collaborators after submission
    setCollaborators([]);

    // Note: Form data will be cleared by useEffect when submission status updates
  }, [submissionData, collaborators, userCurrentContent, validateUrlLocal, urlError, userSubmissionStatus, addSubmission, updateSubmission]);

  // Manage form data based on submission status
  useEffect(() => {
    if (userCurrentContent && currentContentType === 'submissionForm') {
      const submissionStatus = userSubmissionStatus[userCurrentContent._id];

      // Don't do anything if we don't have status data yet
      if (!submissionStatus) {
        return;
      }

      // Handle different submission states
      if (submissionStatus.hasSubmission) {
        const { submission } = submissionStatus;

        // For rejected or more_info_needed submissions, pre-fill the form for resubmission
        if (submission.status === 'rejected' || submission.status === 'more_info_needed') {
          setSubmissionData({
            link: submission.link || '',
            comment: submission.comment || '',
            achievementImages: submission.achievementImages?.length ? submission.achievementImages : (submission.achievementImage ? [submission.achievementImage] : [])
          });
        }
        // For approved submissions, clear the form
        else if (submission.status === 'approved') {
          setSubmissionData({ link: '', comment: '', achievementImages: [] });
        }
        // For pending submissions, keep form data but don't clear (student may want to edit)
        else if (submission.status === 'pending') {
          // Only set if form is currently empty (don't overwrite active edits)
          if (!submissionData.link && !submissionData.comment && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) {
            setSubmissionData({
              link: submission.link || '',
              comment: submission.comment || '',
              achievementImages: submission.achievementImages?.length ? submission.achievementImages : (submission.achievementImage ? [submission.achievementImage] : [])
            });
          }
        }
      } else {
        // No submission exists, clear form for fresh start
        setSubmissionData({ link: '', comment: '', achievementImages: [] });
      }
    }
  }, [userSubmissionStatus, userCurrentContent, currentContentType]);

  // Reset loading state when submission status is received
  useEffect(() => {
    if (userCurrentContent && currentContentType === 'submissionForm') {
      const statusExists = userSubmissionStatus[userCurrentContent._id];

      // Reset loading states when we have status data
      if (statusExists !== undefined) {
        // Always clear status loading once we have data
        setSubmissionStatusLoading(false);

        // Only reset submission states if we were actively waiting
        if (isWaitingForStatus && statusExists?.hasSubmission) {
          // Clear timeout when we get proper status
          if (submittingTimeoutRef.current) {
            clearTimeout(submittingTimeoutRef.current);
            submittingTimeoutRef.current = null;
          }

          setIsSubmitting(false);
          setIsWaitingForStatus(false);
        }
      }
    }
  }, [userSubmissionStatus, userCurrentContent, currentContentType, isWaitingForStatus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submittingTimeoutRef.current) {
        clearTimeout(submittingTimeoutRef.current);
      }
    };
  }, []);

  // Check if submit button should be disabled
  const isSubmitDisabled = (
    (userCurrentContent?.linkRequired && !submissionData.link.trim()) ||
    (userCurrentContent?.commentRequired && !submissionData.comment.trim()) ||
    (userCurrentContent?.achievementImageRequired && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) ||
    urlError
  );

  return {
    // Form state
    submissionData,
    setSubmissionData,
    urlError,
    setUrlError,
    submissionStatusLoading,
    setSubmissionStatusLoading,
    isSubmitting,
    setIsSubmitting,
    isWaitingForStatus,
    collaborators,
    setCollaborators,

    // Validation and helpers
    validateUrlLocal,
    handleUrlChange,
    getCurrentSubmissionStatus,
    canProgressFromSubmission,
    isSubmitDisabled,

    // Form handlers
    handleSubmitSubmission
  };
};